/**
 * Support Ticket Detail Page
 */

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heading, Text } from '@/components/ui/typography';
import {
  TicketPriorityBadge,
  TicketRating,
  TicketReplyForm,
  TicketStatusBadge,
  TicketTimeline,
  useTicketDetail,
} from '@/features/support';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Page } from '@supabank/ui';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { Link, useParams } from 'react-router-dom';

export default function SupportTicketDetail() {
  const { ticketId } = useParams<{ ticketId: string }>();

  const {
    ticket,
    replies,
    isLoading,
    isSending,
    error,
    refresh,
    sendReply,
    submitRating,
  } = useTicketDetail({ ticketId: ticketId || '' });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  // Loading State
  if (isLoading && !ticket) {
    return (
      <Page data-ux-contract="canon" className="bg-background min-h-screen">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex flex-col items-center justify-center py-12">
            <InlineLoadingIndicator size="lg" className="mb-4" />
            <Text color="muted">Loading ticket...</Text>
          </div>
        </div>
      </Page>
    );
  }

  // Error State
  if (error || !ticket) {
    return (
      <Page data-ux-contract="canon" className="bg-background min-h-screen">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Link
            to="/support/tickets"
            className={cn(
              'inline-flex items-center gap-2 mb-6',
              'text-muted-foreground hover:text-foreground',
              semanticTokens.motion.fast
            )}
          >
            <SFIcon icon={sfIcons.back} className="h-4 w-4" />
            Back to tickets
          </Link>

          <Card className="p-12 text-center">
            <div className={cn(
              'w-16 h-16 mx-auto mb-4 rounded-full',
              'bg-destructive/10 flex items-center justify-center'
            )}>
              <SFIcon icon={sfIcons.error} className="h-8 w-8 text-destructive" />
            </div>
            <Heading variant="h3" className="mb-2">Ticket not found</Heading>
            <Text color="muted" className="mb-6">
              {error || 'This ticket may have been deleted or you may not have access to it.'}
            </Text>
            <Button variant="outline" asChild>
              <Link to="/support/tickets">View All Tickets</Link>
            </Button>
          </Card>
        </div>
      </Page>
    );
  }

  const canReply = !['resolved', 'closed'].includes(ticket.status);
  const showRating = ['resolved', 'closed'].includes(ticket.status) && !ticket.satisfaction_rating;

  return (
    <Page data-ux-contract="canon" className="bg-background min-h-screen">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/support/tickets"
            className={cn(
              'inline-flex items-center gap-2',
              'text-muted-foreground hover:text-foreground',
              semanticTokens.motion.fast
            )}
          >
            <SFIcon icon={sfIcons.back} className="h-4 w-4" />
            Back to tickets
          </Link>

          <Button
            variant="ghost"
            size="icon"
            onClick={refresh}
            disabled={isLoading}
          >
            <SFIcon icon={sfIcons.refresh} className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          </Button>
        </div>

        {/* Ticket Header */}
        <Card className="p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <TicketStatusBadge status={ticket.status} />
                {ticket.priority !== 'normal' && (
                  <TicketPriorityBadge priority={ticket.priority} />
                )}
              </div>
              <Heading variant="h2">{ticket.subject}</Heading>
            </div>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span>Created: {formatDate(ticket.created_at)}</span>
            {ticket.first_response_at && (
              <span>First response: {formatDate(ticket.first_response_at)}</span>
            )}
            {ticket.resolved_at && (
              <span>Resolved: {formatDate(ticket.resolved_at)}</span>
            )}
          </div>
        </Card>

        {/* Rating (if resolved and not rated) */}
        {showRating && (
          <div className="mb-6">
            <TicketRating
              currentRating={ticket.satisfaction_rating}
              onSubmit={submitRating}
            />
          </div>
        )}

        {/* Conversation */}
        <Card className="p-6 mb-6">
          <Heading variant="h4" className="mb-4">Conversation</Heading>
          <TicketTimeline
            replies={replies}
            initialMessage={ticket.message}
            initialDate={ticket.created_at}
          />
        </Card>

        {/* Reply Form */}
        {canReply ? (
          <Card className="p-4">
            <TicketReplyForm
              onSubmit={sendReply}
              isLoading={isSending}
              placeholder="Type your message..."
            />
          </Card>
        ) : (
          <Card className="p-4 text-center bg-muted/50">
            <Text variant="sm" color="muted">
              This ticket has been {ticket.status}. If you need further assistance, please create a new ticket.
            </Text>
          </Card>
        )}
      </div>
    </Page>
  );
}
